import {
  Box,
  Container,
  Flex,
  Grid,
  Text,
  Image,
  Link,
} from "@theme-ui/components"
import React from "react"
import { useCompany } from "../hooks/useCompany"
import { useFooter } from "../hooks/useFooter"
import { MagicLink } from "../utils/magicLink"
import { i18nContext } from "../context/i18nContext"
import Logo from "../assets/images/logo.inline.svg"
import ForumLogo from "../assets/images/forum-logo.png"
import { InboundLink } from "./link"
import { getHomePath } from "../utils/path"
import { useSocial } from "../hooks/useSocial"
import { Facebook, Instagram, Linkedin, Youtube } from "react-feather"
import { getPrivacyPolicyId } from "../utils/privacy"

const Footer = () => {
  const { footer } = useFooter()
  const { legalName, vatId, shareCapital, registrationNumber, description } =
    useCompany()
  const social = useSocial()

  footer.forEach(footerItem => {
    footerItem.treeChildren.sort((a, b) => a.position - b.position)
    footerItem.treeChildren.forEach(child => {
      if (child.treeChildren.length > 0) {
        child.treeChildren.sort((a, b) => a.position - b.position)
      }
    })
  })

  return (
    <Box as="footer" sx={{ backgroundColor: "primary", py: 5, color: "light" }}>
      <i18nContext.Consumer>
        {t => (
          <Container variant="fw">
            <Grid
              columns={["1fr", "1fr", "1fr 1.5fr"]}
              sx={{
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
              gap={[0, 4]}
            >
              <Flex
                sx={{
                  flexDirection: "column",
                  p: { fontSize: 1 },
                  "*:first-of-type": { mt: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Flex sx={{ flexDirection: "column" }}>
                    <Box
                      sx={{
                        mb: 3,
                        svg: {
                          width: "auto",
                          height: "40px",
                          path: {
                            fill: "light",
                          },
                        },
                      }}
                    >
                      <InboundLink to={getHomePath(t.locale)}>
                        <Logo />
                      </InboundLink>
                    </Box>
                    <Image src={ForumLogo} sx={{ height: "50px", mb: 3 }} />
                  </Flex>
                  <Box sx={{ mt: [4] }}>
                    {description && (
                      <Box
                        sx={{
                          mb: [4, 4, 4],
                          h2: {
                            mb: [1, 1, 1],
                          },
                          p: {
                            mt: [0, 0, 0],
                            fontSize: [2],
                            fontWeight: "300",
                            lineHeight: [1.5, 1.5, 1.5],
                          },
                          a: {
                            color: "light",
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: description,
                        }}
                      />
                    )}
                    <Flex
                      sx={{
                        display: "flex",
                        py: 1,
                        justifyContent: "space-between",
                        alignContent: "center",
                        flexWrap: "wrap",
                        alignItems: "center",
                        mt: [3, 4],
                      }}
                    >
                      <Box>
                        <Grid columns={[4]} gap={[4]} sx={{ mt: [2, 0] }}>
                          {social.linkedin && (
                            <Box>
                              <MagicLink target="_blank" href={social.linkedin}>
                                <Linkedin color="#FFF" />
                              </MagicLink>
                            </Box>
                          )}
                          {social.facebook && (
                            <Box>
                              <MagicLink target="_blank" href={social.facebook}>
                                <Facebook color="#FFF" />
                              </MagicLink>
                            </Box>
                          )}
                          {social.instagram && (
                            <Box>
                              <MagicLink
                                target="_blank"
                                href={social.instagram}
                              >
                                <Instagram color="#FFF" />
                              </MagicLink>
                            </Box>
                          )}
                          {social.youtube && (
                            <Box>
                              <MagicLink target="_blank" href={social.youtube}>
                                <Youtube color="#FFF" />
                              </MagicLink>
                            </Box>
                          )}
                        </Grid>
                      </Box>
                      <Box
                        sx={{
                          span: { color: "primary" },
                          fontWeight: "400",
                          fontSize: [1],
                          pt: [3, 0],
                          fontFamily: "Teko",
                        }}
                        dangerouslySetInnerHTML={{ __html: t.copyright }}
                      ></Box>
                    </Flex>
                  </Box>
                </Box>
              </Flex>
              <Box>
                <Grid
                  columns={[1, 2, 3]}
                  sx={{
                    mt: [4, 0],
                  }}
                >
                  {footer.map(column => (
                    <List key={column.id}>
                      <Box sx={{ pb: [3] }}>
                        <Text
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: "bold",
                          }}
                        >
                          {column.anchor}
                        </Text>
                      </Box>
                      {column.treeChildren.length > 0 &&
                        column.treeChildren.map(link => (
                          <Item>
                            <Text
                              sx={{
                                display: "block",
                                fontSize: [2],
                                lineHeight: "35px",
                                fontWeight: "300",
                                a: {
                                  color: "light",
                                },
                                "a:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              {link.link ? (
                                <MagicLink item={link.link} />
                              ) : (
                                link.anchor
                              )}
                            </Text>
                            {link.treeChildren.length > 0 && (
                              <List key={link.id}>
                                {link.treeChildren.map(link => (
                                  <Item>
                                    {link.link ? (
                                      <MagicLink
                                        item={link.link}
                                        sx={{
                                          fontSize: [""],
                                        }}
                                      />
                                    ) : (
                                      <Text
                                        sx={{
                                          fontSize: ["4"],
                                        }}
                                      >
                                        {link.anchor}
                                      </Text>
                                    )}
                                  </Item>
                                ))}
                              </List>
                            )}
                          </Item>
                        ))}
                    </List>
                  ))}
                </Grid>
                <Box sx={{ mt: [4, 6] }}>
                  <Text
                    as="p"
                    sx={{ fontSize: [1], fontWeight: "300", mb: [1] }}
                  >
                    © {new Date().getFullYear()} {legalName} |
                    {vatId && ` P.IVA ${vatId}`}{" "}
                    {shareCapital && ` C.S. ${shareCapital}€`}
                    {registrationNumber && `- REA ${registrationNumber}`}{" "}
                  </Text>
                  <Box sx={{ fontSize: [1], fontWeight: "300" }}>
                    <Text
                      as="span"
                      sx={{ fontSize: [1], fontWeight: "300", mr: [3] }}
                    >
                      Tutti i Diritti Riservati / All rights reserved
                    </Text>
                    <Link
                      href={`https://www.iubenda.com/privacy-policy/${
                        getPrivacyPolicyId(t.locale)
                      }`}
                      title="Privacy Policy"
                      sx={{ color: "light" }}
                      target="_blank"
                      rel="noopener nofollow"
                    >
                      Privacy Policy
                    </Link>
                    <Link
                      href={`https://www.iubenda.com/privacy-policy/${
                        getPrivacyPolicyId(t.locale)
                      }/cookie-policy`}
                      title="Cookie Policy"
                      sx={{ color: "light", ml: [3] }}
                      target="_blank"
                      rel="noopener nofollow"
                    >
                      Cookie Policy
                    </Link>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Container>
        )}
      </i18nContext.Consumer>
    </Box>
  )
}

const List = props => {
  return (
    <Flex
      {...props}
      sx={{
        flexDirection: "column",
        margin: 0,
        padding: 0,
        mb: 4,
        listStyle: "none",
        a: {
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      }}
      as="ul"
    />
  )
}

const Item = props => {
  return <Box {...props} as="li" sx={{ mb: 0 }} />
}

export default Footer
